import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import { Box, Link as MuiLink } from "@mui/material";
import { styled } from "@mui/material/styles";
import usePagination from "@mui/material/usePagination";
import classnames from "classnames";
import Link from "next/link";
import * as React from "react";

const List = styled("ul")({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
});

const PageButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "0px",
  background: "#ffffff",
  borderRadius: "100%",
  cursor: "pointer",
  "&.active": {
    fontWeight: 600,
    background: theme.palette.grey[300],
  },
  "&:not(.active)": {
    "&:not(:disabled)": {
      "&:hover": {
        background: theme.palette.grey[200],
      },
    },
  },
  "&:disabled": {
    color: theme.palette.grey[400],
  },
}));

type Props = {
  hrefPrefix?: string;
  onChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    from: number;
    to: number;
    total: number;
  };
};

export default function Pagination({
  hrefPrefix,
  pagination,
  onChange,
}: Props) {
  const { items } = usePagination({
    count: pagination.last_page,
    page: pagination.current_page,
    onChange,
  });

  const paginations = React.useMemo(() => {
    return items.map(({ page, type, selected, disabled, onClick }) => {
      let inner: React.ReactElement = (
        <PageButton
          onClick={onClick}
          disabled={disabled}
          className={classnames({ ["active"]: selected })}
        >
          {page}
        </PageButton>
      );
      let href = null;
      if (type === "page") {
        if (page === 1) {
          href = hrefPrefix ? `${hrefPrefix}` : null;
        } else {
          href = hrefPrefix ? `${hrefPrefix}?page=${page}` : null;
        }
      }
      if (type === "start-ellipsis" || type === "end-ellipsis") {
        inner = (
          <Box
            sx={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            …
          </Box>
        );
      }
      if (type === "previous") {
        if (!disabled) {
          if (page === 1) {
            href = hrefPrefix ? `${hrefPrefix}` : null;
          } else {
            href = hrefPrefix ? `${hrefPrefix}?page=${page}` : null;
          }
        }
        inner = (
          <PageButton onClick={onClick} disabled={disabled}>
            <ChevronLeftIcon />
          </PageButton>
        );
      }
      if (type === "next") {
        if (!disabled) {
          href = hrefPrefix ? `${hrefPrefix}?page=${page}` : null;
        }
        inner = (
          <PageButton onClick={onClick} disabled={disabled}>
            <ChevronRightIcon />
          </PageButton>
        );
      }
      return {
        inner,
        type,
        href,
        selected,
        disabled,
      };
    });
  }, [items, pagination]);

  return (
    <nav>
      <List>
        {paginations.map(({ inner, href, selected, disabled }, index) => {
          let children = null;
          if (href) {
            children = (
              <Link href={href} passHref>
                <MuiLink underline="none" color="inherit">
                  {inner}
                </MuiLink>
              </Link>
            );
          } else {
            children = inner;
          }
          return (
            <Box
              component="li"
              key={index}
              sx={{
                mx: 0.5,
              }}
            >
              {children}
            </Box>
          );
        })}
      </List>
    </nav>
  );
}
